// Progress from the Learn topics (Years 6–12). Each topic page saves its own state under lumio-pkg:<topic id>
// (a lesson is done once its step reaches 7) and keeps a summary in lumio-review-index:
// { [topic id]: { title, level, due, next, mastered, points, t } }. Both keys sync with the account (accountSync.js),
// so a parent sees the same progress on any device. Progress is per child: like the app's profileKey, the first child
// (empty id) uses the plain keys and each later child <key>_<their id>. Replaces the old Year 10 science tracking.

const LESSON_XP = 100; // a completed 30–40 minute lesson, on the same scale as a practice session (10 per correct + 2 per question)
const POINT_XP = 25;   // a syllabus point mastered

const SUBJECT_LABEL = {
  english: 'English', maths: 'Maths', mathematics: 'Maths', science: 'Science', biology: 'Biology', chemistry: 'Chemistry',
  physics: 'Physics', earthspace: 'Earth and Space', hass: 'HASS', history: 'History', geography: 'Geography',
  civics: 'Civics and Citizenship', economics: 'Economics and Business', hpe: 'Health and PE', digitaltech: 'Digital Technologies',
  'english-advanced': 'English Advanced', 'english-standard': 'English Standard', 'mathematics-advanced': 'Maths Advanced',
  'mathematics-standard': 'Maths Standard', 'mathematics-extension-1': 'Maths Extension 1', 'specialist-mathematics': 'Specialist Maths',
  'mathematics-extension-2': 'Maths Extension 2', 'english-eald': 'English EAL/D', 'english-extension': 'English Extension',
  'english-studies': 'English Studies', 'ancient-history': 'Ancient History', 'modern-history': 'Modern History',
  'business-studies': 'Business Studies', 'legal-studies': 'Legal Studies', cafs: 'Community and Family Studies',
  'health-and-movement-science': 'Health and Movement Science', 'earth-and-environmental-science': 'Earth and Environmental Science',
  'investigating-science': 'Investigating Science', 'society-and-culture': 'Society and Culture', 'studies-of-religion': 'Studies of Religion',
  'enterprise-computing': 'Enterprise Computing', 'software-engineering': 'Software Engineering',
  'vce-ancient-history': 'VCE Ancient History', 'vce-australian-history': 'VCE Australian History', 'vce-biology': 'VCE Biology',
  'vce-business-management': 'VCE Business Management', 'vce-chemistry': 'VCE Chemistry', 'vce-economics': 'VCE Economics',
  'vce-english': 'VCE English', 'vce-general': 'VCE General Mathematics', 'vce-geography': 'VCE Geography',
  'vce-hhd': 'VCE Health and Human Development', 'vce-legal': 'VCE Legal Studies', 'vce-methods': 'VCE Mathematical Methods',
  'vce-modern-history': 'VCE Modern History', 'vce-pe': 'VCE Physical Education', 'vce-physics': 'VCE Physics',
  'vce-psychology': 'VCE Psychology', 'vce-revolutions': 'VCE History: Revolutions',
};
const SENIOR_LABEL = { economics: 'Economics' }; // Years 11–12 Economics is its own course, not Years 7–10 Economics and Business

const pk = (key, profileId) => (profileId ? `${key}_${profileId}` : key);
const readJson = (key, fallback) => { try { return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback; } catch (_) { return fallback; } };

// y7-history-u02 → Year 7 History; biology-y11-01 → Year 11 Biology; english-standard-y12-03-d1 → Year 12 English Standard
export function topicInfo(id) {
  let m = /^y(\d+)-([a-z]+)-u\d+$/.exec(id);
  if (m) return { year: +m[1], subject: m[2] };
  m = /^(.+?)-y(1[12])-/.exec(id);
  if (m) return { year: +m[2], subject: m[1] };
  m = /^y(1[12])-([a-z]+)/.exec(id); // Years 11–12 pilot topics such as y11-english-lawson
  if (m) return { year: +m[1], subject: ({ maths: 'mathematics-advanced', chem: 'chemistry', english: 'english-advanced' })[m[2]] || m[2] };
  return null;
}

export const subjectLabel = (subject, year = 0) => (year >= 11 && SENIOR_LABEL[subject]) || SUBJECT_LABEL[subject] || subject.replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

// Every topic the child has opened, with its progress.
export function readTopics(profileId = '') {
  const idx = readJson(pk('lumio-review-index', profileId), {}) || {};
  const out = [];
  for (const [id, s] of Object.entries(idx)) {
    const info = topicInfo(id);
    if (!info || !s) continue;
    const st = readJson(pk(`lumio-pkg:${id}`, profileId), null);
    const lessonsDone = st && st.lessons ? Object.values(st.lessons).filter((l) => l && l.reached >= 7).length : 0;
    out.push({ id, title: s.title || id, ...info, mastered: s.mastered || 0, points: s.points || 0, due: s.due || 0, t: s.t || 0, lessonsDone, href: `/lessons/topics/${id}.html` });
  }
  return out;
}

// One summary per year and subject, most recent first, each with its topics and up to three focus areas
// (reviews that are due first, then started topics with the least mastered).
export function learnSummaries(profileId = '') {
  const groups = {};
  for (const tp of readTopics(profileId)) {
    const key = `${tp.year}|${tp.subject}`;
    const g = groups[key] || (groups[key] = { key, year: tp.year, subject: tp.subject, label: `Year ${tp.year} ${subjectLabel(tp.subject, tp.year)}`, topics: [], lessonsDone: 0, mastered: 0, points: 0, due: 0, t: 0 });
    g.topics.push(tp); g.lessonsDone += tp.lessonsDone; g.mastered += tp.mastered; g.points += tp.points; g.due += tp.due; g.t = Math.max(g.t, tp.t);
  }
  return Object.values(groups).map((g) => {
    const ratio = (tp) => (tp.points ? tp.mastered / tp.points : 1);
    const focus = g.topics
      .filter((tp) => tp.due > 0 || (tp.points > 0 && ratio(tp) < 0.5))
      .sort((a, b) => b.due - a.due || ratio(a) - ratio(b))
      .slice(0, 3)
      .map((tp) => ({ id: tp.id, label: tp.title, href: tp.href, reason: tp.due > 0 ? `${tp.due} review${tp.due === 1 ? '' : 's'} due` : `${tp.mastered} of ${tp.points} points mastered` }));
    const pct = g.points ? Math.round((g.mastered / g.points) * 100) : 0;
    return { ...g, topics: g.topics.sort((a, b) => b.t - a.t), pct, focus };
  }).sort((a, b) => b.t - a.t);
}

// XP earned in Learn: total, and this month's share (topics last worked on this month).
export function learnXP(profileId = '') {
  const month = new Date().toISOString().slice(0, 7);
  let total = 0, monthly = 0;
  for (const tp of readTopics(profileId)) {
    const xp = tp.lessonsDone * LESSON_XP + tp.mastered * POINT_XP;
    total += xp;
    if (tp.t && new Date(tp.t).toISOString().slice(0, 7) === month) monthly += xp;
  }
  return { total, monthly };
}
